import { FC, SyntheticEvent, useMemo } from 'react'
import { FilterCriteriaValueFieldProps } from './FilterCriteriaValueFieldProps'
import { Autocomplete, Box, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { EntityQueries } from '@portal/pkg-filter-criteria/data'
import { EntityTypeAvatar } from '@portal/pkg-filter-criteria/components'
import { EntityTypeQueries } from '@portal/pkg-filter-criteria/data'
import { Loading } from '@portal/pkg-ui'
import { useEntityRefSearch } from '@portal/pkg-filter-criteria/helper'

type MinimalEntity = {
  id: number
  entityTypeId: number
  shortName?: string
}

const OptionRenderer: FC<MinimalEntity> = ({ id, entityTypeId, shortName }) => {
  const { data: entityType } = useQuery(EntityTypeQueries.getEntityType(entityTypeId))

  return (
    <Loading until={{ entityType }} fallback={() => <Skeleton variant="rectangular" />}>
      {({ entityType }) => (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <EntityTypeAvatar entityType={entityType} size={24} />
          <Stack
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Typography variant="body2">{shortName}</Typography>
            <Typography variant="body2" color="textSecondary">
              {id}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Loading>
  )
}

export const FilterCriteriaValueEntityRef: FC<FilterCriteriaValueFieldProps> = ({
  assumption,
  fieldProps: { value, onChange, error, ..._fieldProps },
}) => {
  const { t } = useTranslation()

  const selectedEntityId = useMemo(() => {
    try {
      return parseInt(value)
    } catch {
      return null
    }
  }, [value])

  const { data: selectedEntity } = useQuery(EntityQueries.getEntity(selectedEntityId))

  const { options, onSearchChange } = useEntityRefSearch({
    entityRefEntityTypeId: assumption.entityRefEntityTypeId,
  })

  const handleChange = (_event: SyntheticEvent, newValue: MinimalEntity | null) => {
    onChange(newValue ? newValue.id.toString() : '')
  }

  const handleInputChange = (_event: SyntheticEvent, newValue: string) => {
    onSearchChange(newValue)
  }

  return (
    <Autocomplete
      value={selectedEntity ?? null}
      options={options}
      onChange={handleChange}
      onInputChange={handleInputChange}
      noOptionsText={t('filterCriteria.fields.entityref.noOptionsText')}
      getOptionLabel={(e) => e.shortName ?? ''}
      filterOptions={(x) => x} // Override the built-in filter since we filter with an API call.
      renderInput={(params) => <TextField variant="outlined" error={error} {...params} />}
      renderOption={(props, entity) => (
        <Box component="li" {...props}>
          <OptionRenderer {...entity} />
        </Box>
      )}
    />
  )
}
